import { isSafeHttpUrl } from "@redotech/util/link";
import { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as styles from "./ask-redo-markdown.module.css";

/**
 * Markdown renderer for assistant messages.
 *
 * We use `react-markdown` (already in the repo) instead of AI
 * Elements' `streamdown` — streamdown requires Tailwind 4, we're on
 * Tailwind 3 / CSS modules.
 *
 * Plugins:
 *   - `remark-gfm` for GitHub-flavored markdown: tables, task lists,
 *     strikethrough, autolinks. Without it the table styles in
 *     `ask-redo-markdown.module.css` would never trigger because
 *     ReactMarkdown's base CommonMark parser doesn't recognize
 *     `|`-pipe tables at all. Same plugin the legacy
 *     `message-concierge-bubble` uses.
 *
 * Raw HTML inside markdown stays disabled (the `react-markdown`
 * default), so a hostile model output can't inject scripts or
 * arbitrary tags into the merchant DOM. We override `a` and `img`
 * explicitly: links are forced to `target="_blank" rel="noopener
 * noreferrer"`, and both anchors and images go through an http(s)
 * scheme guard so a model-emitted `javascript:` or `data:` URL
 * renders inert rather than executing in the merchant origin.
 */
export const AskRedoMarkdown = memo(function AskRedoMarkdown({
  content,
}: {
  content: string;
}) {
  // Stable references so ReactMarkdown can short-circuit its
  // internal parser memoisation.
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const components = useMemo(
    () => ({
      a: ({ href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
        isSafeHttpUrl(href) ? (
          <a {...props} href={href} rel="noopener noreferrer" target="_blank" />
        ) : (
          // Drop unsafe href entirely; render the inner text/markup
          // as a plain span so the user still sees the label.
          <span {...props} />
        ),
      img: ({
        src,
        alt,
        ...props
      }: React.ImgHTMLAttributes<HTMLImageElement>) =>
        isSafeHttpUrl(src) ? (
          <img
            {...props}
            alt={alt ?? ""}
            decoding="async"
            loading="lazy"
            referrerPolicy="no-referrer"
            src={src}
          />
        ) : null,
      table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
        <div className={styles.tableWrapper}>
          <table {...props} />
        </div>
      ),
    }),
    [],
  );

  return (
    <div className={styles.markdown}>
      <ReactMarkdown components={components} remarkPlugins={remarkPlugins}>
        {content}
      </ReactMarkdown>
    </div>
  );
});
